import useSWR from 'swr';
import { clamp } from 'lodash';
import { useCallback, useContext, useEffect, useState } from 'react';
import { AppContext } from 'providers/AppProvider';
import { baseAction } from 'store/actions';
import { FLOW_TYPE } from 'consts';
import { FlowTypeNumber, IsDisabled, nextBTNLabel, transformRiskq } from 'helpers';
import { Questionnaire, TestType } from 'types';

export function useRiskq(testTypeInt: number) {
	const ctx = useContext(AppContext);
	const [isDisabled, setIsDisabled] = useState(true);
	const [questionnaire, setQuestionnaire] = useState<Questionnaire | undefined>();
	const [pageIndex, setPageIndex] = useState(0);
	const { config, dispatch, entityType, fetchRelations, flowType, http, state, t } = ctx;
	const { API, IS_EMPLOYEE_WEB } = config;
	const { context, riskq, user } = state;
	const processId = context.processInfo?.agreementProcessId;
	const isRRSU = FLOW_TYPE.RR_SU === flowType;
	const employeeWebSSN = isRRSU ? `/${context.kennitala}` : `/${user.kennitala}`;
	const ssn = IS_EMPLOYEE_WEB ? employeeWebSSN : '';
	const url = `${API.riskq}/${processId}/questionnaire/${testTypeInt}/${FlowTypeNumber(flowType)}${ssn}`;
	const { data } = useSWR<Questionnaire>(url);
	const group = questionnaire?.questionGroups[pageIndex];
	const lastPageIndex = questionnaire ? questionnaire.questionGroups.length - 1 : 0;
	const title = t(`${flowType}_${entityType}_suitability_page_${pageIndex}_title`, 'riskq');
	const testType = TestType.Appropriateness.toLowerCase();
	const prefix = `${flowType}_${entityType}_${testType}_page_${pageIndex + 1}`;
	const isLastPage = isRRSU ? false : pageIndex === lastPageIndex;
	const page = `${testType}_${pageIndex}`;
	const next = nextBTNLabel(flowType, isLastPage, t);
	const prev = 0 === pageIndex ? undefined : t('back');

	const handlePrev = useCallback(() => {
		const index = clamp(pageIndex - 1, 0, lastPageIndex);
		setPageIndex(index);
	}, [pageIndex, lastPageIndex]);

	const handleNext = useCallback(async () => {
		const index = pageIndex + 1;
		if (index <= lastPageIndex) {
			setPageIndex(index);
		} else {
			const payload = { answers: riskq };
			await http.post(url, payload);
			dispatch([baseAction, { riskq: [] }]);
			await fetchRelations(true);
		}
	}, [pageIndex, lastPageIndex, riskq, http, url, fetchRelations, dispatch]);

	useEffect(() => {
		dispatch([baseAction, { riskq: [] }]);
	}, []); // eslint-disable-line

	useEffect(() => {
		setIsDisabled(IsDisabled());
	}, [riskq, pageIndex]);

	useEffect(() => {
		data && setQuestionnaire(transformRiskq(data));
	}, [data]); // eslint-disable-line

	return {
		...ctx,
		data: questionnaire,
		group,
		handleNext,
		handlePrev,
		isDisabled,
		isLastPage,
		next,
		page,
		prefix,
		prev,
		testType,
		title
	};
}
